import { apiClient } from '@/core/infra_api_client'

// ============= Types =============

export type ImageStatus = 'building' | 'available' | 'failed' | 'deprecated' | 'deleted'

export interface PackerImage {
  id: string
  templateId: string
  name: string
  cloudProvider: string
  region: string
  imageId: string
  version: string
  osVersion: string
  binaryVersions: Record<string, string>
  status: ImageStatus
  buildOperationId?: string
  createdAt: string
  updatedAt: string
}

export interface PackerTemplate {
  id: string
  name: string
  description: string
  cloudProvider: string
  imageType: string
  repoPath: string
  defaultRegion: string
  variables: TemplateVariable[]
}

export interface TemplateVariable {
  name: string
  description: string
  default: string
  required: boolean
  sensitive: boolean
}

export interface ImageDeviation {
  id: string
  templateId: string
  cloudProvider: string
  region: string
  deviationType: 'outdated' | 'missing' | 'deprecated'
  currentImageId?: string
  details: Record<string, unknown>
  detectedAt: string
  resolvedAt?: string
}

export interface TriggerBuildRequest {
  templateId: string
  version: string
  credentialId: string
  region?: string
  aptChannel?: string
  variables?: Record<string, string>
}

export interface TriggerBuildResponse {
  imageId: string
  operationId: string
  status: string
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  perPage: number
  totalPages: number
}

export interface RegistryBuildStatus {
  imageId: string
  status: string
  cloudImageId?: string
  createdAt: string
}

export interface RegistryOverview {
  version: string
  cloud: string
  digest: string
  pushedAt: string
  sizeBytes: number
  build: RegistryBuildStatus | null
}

// ============= API =============

const BASE_PATH = 'images'

export async function listImages(params?: {
  page?: number
  perPage?: number
  status?: string
  cloudProvider?: string
  templateId?: string
}): Promise<PaginatedResponse<PackerImage>> {
  const searchParams: Record<string, string | number | boolean> = {}
  if (params?.page) searchParams['page'] = params.page
  if (params?.perPage) searchParams['per_page'] = params.perPage
  if (params?.status) searchParams['status'] = params.status
  if (params?.cloudProvider) searchParams['cloud_provider'] = params.cloudProvider
  if (params?.templateId) searchParams['template_id'] = params.templateId

  return apiClient.get<PaginatedResponse<PackerImage>>(BASE_PATH, searchParams)
}

export async function getImage(id: string): Promise<PackerImage> {
  return apiClient.get<PackerImage>(`${BASE_PATH}/${id}`)
}

export async function listTemplates(): Promise<PackerTemplate[]> {
  return apiClient.get<PackerTemplate[]>(`${BASE_PATH}/templates`)
}

export async function triggerBuild(data: TriggerBuildRequest): Promise<TriggerBuildResponse> {
  return apiClient.post<TriggerBuildResponse, TriggerBuildRequest>(`${BASE_PATH}/build`, data)
}

export async function listDeviations(params?: {
  page?: number
  perPage?: number
  templateId?: string
  deviationType?: string
}): Promise<PaginatedResponse<ImageDeviation>> {
  const searchParams: Record<string, string | number | boolean> = {}
  if (params?.page) searchParams['page'] = params.page
  if (params?.perPage) searchParams['per_page'] = params.perPage
  if (params?.templateId) searchParams['template_id'] = params.templateId
  if (params?.deviationType) searchParams['deviation_type'] = params.deviationType

  return apiClient.get<PaginatedResponse<ImageDeviation>>(`${BASE_PATH}/deviations`, searchParams)
}

export async function refreshDeviations(): Promise<{ deviationsFound: number; status: string }> {
  return apiClient.post<{ deviationsFound: number; status: string }, undefined>(
    `${BASE_PATH}/deviations/refresh`,
    undefined,
  )
}

export async function listRegistryImages(): Promise<RegistryOverview[]> {
  return apiClient.get<RegistryOverview[]>(`${BASE_PATH}/registry`)
}
